import { useEffect, useRef } from 'react';
import { CommonItem, GameState, Investigator, Monster, UserAction } from '../api';
import { findInvestigator } from '../utils/investigators';
import type { StackedItemWithKind } from './Card';
import { getItemKindFromCardType } from './StackedItemCard';
import { isSkillCheckProgression } from './SkillCheckActionControls';
import { isControllerAction, isUndoAction } from './UpkeepActions';

export const monsterChoice = (action: UserAction): Monster | undefined =>
  /^fight_monster_\d+_/.test(action.id) && action.cardType === 'Monster' && action.card && typeof action.card === 'object'
    && !Array.isArray(action.card) && 'name' in action.card ? action.card as Monster : undefined;

export const fightControlKind = (action: UserAction, monster?: Monster | null) =>
  isUndoAction(action) ? 'undo' : isSkillCheckProgression(action) ? 'continue' : monster && action.id === `fight_monster_${monster.name}` ? 'fight'
    : monster && action.id === `evade_monster_${monster.name}` ? 'evade' : undefined;

export function useFightInvestigator(state: GameState, actions: UserAction[], enabled: boolean, viewerName: string) {
  const remembered = useRef<{ scope: string; name: string } | undefined>(undefined);
  const scope = JSON.stringify([state.gameId, state.currentTurn, state.currentPhase, viewerName.toLowerCase()]);
  const current = state.currentFightingMonster;
  const choices = actions.filter(action => !!monsterChoice(action));
  const context = enabled && !state.isFinalBattle && (!!current || choices.length > 0);
  const uniqueName = (candidates: UserAction[]) => {
    const names = Array.from(new Set(candidates.filter(action => isControllerAction(action, viewerName))
      .map(action => action.investigatorName?.toLowerCase()).filter((name): name is string => !!name)));
    return names.length === 1 ? names[0] : undefined;
  };
  const explicit = uniqueName(choices.length ? choices : actions.filter(action => ['fight', 'evade'].includes(fightControlKind(action, current) ?? '')));
  const check = state.skillCheckInformation;
  const checkName = check && ['Combat', 'Horror', 'Evade'].includes(check.skillName ?? '')
    && (!check.monsterName || check.monsterName === current?.name) ? check.investigatorName : undefined;
  const name = context ? explicit ?? (remembered.current?.scope === scope ? remembered.current.name : undefined) ?? checkName : undefined;
  const investigator = findInvestigator(state, name);
  useEffect(() => {
    if (!enabled || remembered.current?.scope !== scope || (!current && !state.currentCombatMonsters?.length && !choices.length)) remembered.current = undefined;
    if (context && investigator) remembered.current = { scope, name: investigator.name };
  }, [enabled, context, current, state.currentCombatMonsters, choices.length, scope, investigator]);
  return investigator;
}

export function remainingFightMonsters(monsters: Monster[], current?: Monster | null): Monster[] {
  if (!current) return monsters;
  let index = monsters.findIndex(monster => JSON.stringify(monster) === JSON.stringify(current));
  if (index < 0) index = monsters.findIndex(monster => monster.name === current.name && monster.dimensionSign === current.dimensionSign);
  return monsters.filter((_, candidate) => candidate !== index);
}

export function fightEquipment(investigator: Investigator, actions: UserAction[]) {
  const inventory: StackedItemWithKind[] = [
    ...(investigator.commonItems ?? []).map(item => ({ item, kind: 'common' as const })),
    ...(investigator.uniqueItems ?? []).map(item => ({ item, kind: 'unique' as const })),
    ...(investigator.spells ?? []).map(item => ({ item, kind: 'spell' as const })),
    ...(investigator.specialCards ?? []).map(item => ({ item, kind: 'special' as const })),
  ];
  const entries = inventory.filter(({ item }) => {
    const equipment = item as CommonItem;
    return equipment.equipped || (equipment.hands != null && equipment.hands >= 0);
  });
  const proposals = entries.map(entry => {
    const item = entry.item as CommonItem;
    const toggle = item.equipped ? 'unequip' : 'equip';
    const matching = actions.filter(action => {
      if (!action.id.startsWith(toggle + '_') || action.investigatorName?.toLowerCase() !== investigator.name.toLowerCase()) return false;
      if (item.id && action.id === `${toggle}_${item.id}`) return true;
      if (getItemKindFromCardType(action.cardType) !== entry.kind || !action.card || typeof action.card !== 'object' || Array.isArray(action.card)) return false;
      const card = action.card as CommonItem;
      if (item.id && card.id) return item.id === card.id;
      return !item.id && !card.id && card.name === item.name
        && inventory.filter(candidate => candidate.kind === entry.kind && candidate.item.name === item.name).length === 1;
    });
    return { entry, action: matching.length === 1 ? matching[0] : undefined };
  });
  // An action may only disappear from the fallback list when exactly one card can execute it.
  return proposals.map(proposal => ({ ...proposal,
    action: proposal.action && proposals.filter(other => other.action === proposal.action).length === 1 ? proposal.action : undefined }));
}
